import { useSuspenseQuery } from '@tanstack/react-query';
import { paths } from '@/generated/supplier-experience-api';
import apiClient from '@/lib/apiClient';
import { createTakerMarketUuidsMap } from '@/utils/takerMarketUuidMap';
import { TakerMarket } from './useTakerMarkets';

export type LockedInvoiceStatsRequestBody =
  paths['/locked-invoices/statistics']['post']['requestBody']['content']['application/json'];
export type LockedInvoiceStatsResponse =
  paths['/locked-invoices/statistics']['post']['responses']['200']['content']['application/json'];
export type LockedInvoiceStatsSearchFilters = LockedInvoiceStatsRequestBody['search'];

export interface LockedInvoiceStatsParams {
  makerOrganizationUuid: string;
  search?: LockedInvoiceStatsSearchFilters;
  takerMarkets: TakerMarket[];
}

const fetchLockedInvoiceStats = async ({
  makerOrganizationUuid,
  search,
  takerMarkets,
}: LockedInvoiceStatsParams): Promise<LockedInvoiceStatsResponse['data']> => {
  const response = await apiClient
    .post(`api/supplier-experience/locked-invoices/statistics`, {
      json: {
        makerOrganizationUuid,
        supplierMarkets: takerMarkets.map((market) => ({
          marketUuid: market.marketUuid,
          supplierDivisionUuid: market.takerDivisionUuid,
        })),
        ...(search ? { search } : {}),
      } satisfies LockedInvoiceStatsRequestBody,
    })
    .json<LockedInvoiceStatsResponse>();

  return response.data;
};

const useLockedInvoiceStats = (params: LockedInvoiceStatsParams) => {
  const { makerOrganizationUuid, search, takerMarkets } = params;
  const isValid = !!makerOrganizationUuid && !!takerMarkets.length;
  const takerMarketUuidsMap = createTakerMarketUuidsMap(takerMarkets).sort();

  return useSuspenseQuery({
    queryKey: isValid
      ? ['locked-invoice-stats', makerOrganizationUuid, search, ...takerMarketUuidsMap]
      : ['locked-invoice-stats-invalid'],
    queryFn: () => (isValid ? fetchLockedInvoiceStats(params) : null),
    select: (data) => {
      const { totalAmount, totalCount } = (data?.supplierMarkets ?? []).reduce(
        (acc, sm) => ({
          totalAmount: acc.totalAmount + sm.amount,
          totalCount: acc.totalCount + sm.count,
        }),
        {
          totalAmount: 0,
          totalCount: 0,
        }
      );

      return {
        totalAmount,
        totalCount,
      };
    },
  });
};

export default useLockedInvoiceStats;
